/** Plan values computed from settings.paddle.plans — never from content. RULES-ENGINE.md. */
import type { PlanSetting, Settings } from './types.ts';

export interface PlanValues {
  id: string;
  priceId: string;
  amount: number;
  currency: string;
  weeks: number;
  credits: number;
  /** "$19.99" — the recurring charge */
  price: string;
  /** "$9.99" — first-period charge when the plan has an intro */
  introPrice?: string;
  /** same, but a zero-cost intro reads "Free" */
  introLabel?: string;
  introWeeks?: number;
  /** headline number on the card, split for the design's small-cents type */
  major: string;
  cents: string;
  /** per-week price in cents (floored, as the design shows) */
  perWeek: number;
  perWeekLabel: string;
  /** strike-through anchor, only on intro plans */
  anchor?: string;
  savePct: number;
  hasIntro: boolean;
}

const SYMBOLS: Record<string, string> = { USD: '$', EUR: '€', GBP: '£' };

export function money(cents: number, currency: string): string {
  const symbol = SYMBOLS[currency] ?? `${currency} `;
  return `${symbol}${(cents / 100).toFixed(2)}`;
}

export function splitMoney(cents: number, currency: string): { major: string; cents: string } {
  const s = money(cents, currency);
  const dot = s.lastIndexOf('.');
  return { major: s.slice(0, dot), cents: s.slice(dot) };
}

export function planValues(plan: PlanSetting, settings: Settings): PlanValues {
  const baseline = settings.paddle.plans.find((p) => p.id === settings.paddle.baselinePlan) ?? plan;
  const perWeek = Math.floor(plan.amount / plan.weeks);
  // computed from the exact weekly rates, not the rounded display figures —
  // flooring first inflated this by a point against the app's own number
  const exact = plan.amount / plan.weeks;
  const baseExact = baseline.amount / baseline.weeks;
  const savePct = baseExact > 0 ? Math.max(0, Math.floor((1 - exact / baseExact) * 100)) : 0;

  const freeIntro = !!plan.intro && plan.intro.amount === 0;
  const headline = plan.intro ? Math.round(plan.intro.amount / plan.intro.weeks) : perWeek;
  const { major, cents } = freeIntro ? { major: 'Free', cents: '' } : splitMoney(headline, plan.currency);

  return {
    id: plan.id,
    priceId: plan.priceId[settings.paddle.env === 'production' ? 'live' : 'sandbox'],
    amount: plan.amount,
    currency: plan.currency,
    weeks: plan.weeks,
    credits: plan.credits,
    price: money(plan.amount, plan.currency),
    introPrice: plan.intro ? money(plan.intro.amount, plan.currency) : undefined,
    introLabel: plan.intro ? (freeIntro ? 'Free' : money(plan.intro.amount, plan.currency)) : undefined,
    introWeeks: plan.intro?.weeks,
    major,
    cents,
    perWeek,
    perWeekLabel: money(perWeek, plan.currency),
    anchor: plan.intro ? money(plan.amount, plan.currency) : undefined,
    savePct,
    hasIntro: !!plan.intro,
  };
}

export const allPlanValues = (settings: Settings): PlanValues[] =>
  settings.paddle.plans.map((p) => planValues(p, settings));
